import type {
	FormDataPublic,
	LifecycleHook,
	SupportedFieldProperty,
} from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/types';
import type { AppId, BufferForChangeId } from '../../types';
import { MULTI_APP_CONFLICT_ERROR, MULTI_APP_CONFLICT_CAUSE_OTHER_APPS } from '../../utils/errors';
import type { ConflictError } from '../../utils/errors/types';
import {
	transformFieldError,
	transformScreenTabsError,
} from '../../utils/errors/ui-modifications-multi-app-conflict-error';
import { propertyToMethodMap } from '../../utils/map-field-property-to-api-method';
import { FIELD_ERROR_TYPE, SCREENTABS_ERROR_TYPE } from './constants';
import type {
	ConflictsGroupedByAppId,
	FieldConflict,
	FieldPropertiesChanges,
	ScreenTabsConflict,
} from './types';

export const getConflicts = ({
	bufferForChangeId,
}: {
	bufferForChangeId: BufferForChangeId;
}): ConflictsGroupedByAppId => {
	const fieldsPropsByApps: FieldPropertiesChanges = {};
	const appsChangingScreenTabs: string[] = [];
	Object.entries(bufferForChangeId)
		.filter(([_, allChanges]) => allChanges !== null)
		.forEach(([appId, allChanges]) => {
			if (allChanges?.screenTabs && allChanges.screenTabs.length > 0) {
				appsChangingScreenTabs.push(appId);
			}
			Object.entries(allChanges?.fields ?? {}).forEach(([fieldId, properties]) =>
				Object.keys(properties).forEach((property) => {
					if (!isPropertySupported(property)) {
						return;
					}

					const fieldProperty = `${fieldId}_${property}`;

					if (!fieldsPropsByApps[fieldProperty]) {
						fieldsPropsByApps[fieldProperty] = {
							apps: [],
							fieldId,
							property,
						};
					}

					fieldsPropsByApps[fieldProperty].apps.push(appId);
				}),
			);
		});

	const conflicts: ConflictsGroupedByAppId = {};

	Object.values(fieldsPropsByApps)
		.filter(({ apps }) => apps.length > 1)
		.forEach(({ apps, fieldId, property }) => {
			apps.forEach((appId) => {
				const conflict: FieldConflict = {
					fieldId,
					property,
					type: FIELD_ERROR_TYPE,
				};

				conflicts[appId] = conflicts[appId] ? [...conflicts[appId], conflict] : [conflict];
			});
		});

	if (appsChangingScreenTabs.length > 1) {
		appsChangingScreenTabs.forEach((appId) => {
			const screenTabsConflict: ScreenTabsConflict = {
				type: SCREENTABS_ERROR_TYPE,
			};
			conflicts[appId] = conflicts[appId]
				? [...conflicts[appId], screenTabsConflict]
				: [screenTabsConflict];
		});
	}

	return conflicts;
};

export const getConflictErrorGroups = ({
	appIds,
	conflicts,
	formData,
	lifecycleHook,
}: {
	appIds: AppId[];
	formData: FormDataPublic;
	conflicts: ConflictsGroupedByAppId;
	lifecycleHook: LifecycleHook;
}): Record<AppId, ConflictError[]> => {
	const errorsMap: Record<AppId, ConflictError[]> = {};

	if (Object.keys(conflicts).length === 0) {
		return errorsMap;
	}

	appIds.forEach((appId: AppId) => {
		if (!conflicts[appId]) {
			const appError: ConflictError = {
				type: MULTI_APP_CONFLICT_ERROR,
				cause: MULTI_APP_CONFLICT_CAUSE_OTHER_APPS,
				message: `Due to a conflict between other UIM apps, we couldn’t apply any modifications from ${lifecycleHook}.`,
				lifecycleHook,
			};

			errorsMap[appId] = [appError];
		} else {
			errorsMap[appId] = conflicts[appId].map((conflict) => {
				switch (conflict.type) {
					case SCREENTABS_ERROR_TYPE:
						return transformScreenTabsError({ lifecycleHook });
					case FIELD_ERROR_TYPE:
						return transformFieldError({ lifecycleHook, formData, conflict });
					default:
						throw new Error('Unknown conflict type');
				}
			});
		}
	});

	return errorsMap;
};

export const isPropertySupported = (property: unknown): property is SupportedFieldProperty => {
	if (typeof property !== 'string') {
		return false;
	}

	return Object.prototype.hasOwnProperty.call(propertyToMethodMap, property);
};

export const findAppsWhichDidNotSendChangesBeforeTimeout = (bufferForChangeId: BufferForChangeId) =>
	Object.entries(bufferForChangeId)
		.filter(([_, fieldsChanges]) => fieldsChanges === null)
		.map(([appId]) => appId);
